// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	type ActivityData,
	formatElapsedTime,
	getActivityHeader,
} from '@app/features/presence/state/UserActivity';
import {
	BroadcastIcon,
	ClockIcon,
	CodeIcon,
	GameControllerIcon,
	HeadphonesIcon,
	SpotifyLogoIcon,
	TelevisionIcon,
	TrophyIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';
import styles from './RichPresenceCard.module.css';

interface RichPresenceCardProps {
	activity: ActivityData;
	className?: string;
}

function useElapsedTime(startedAt?: number, showTime: boolean = true): string | null {
	const [timeText, setTimeText] = useState<string | null>(() => {
		if (!startedAt || !showTime) return null;
		return formatElapsedTime(startedAt);
	});

	useEffect(() => {
		if (!startedAt || !showTime) {
			setTimeText(null);
			return;
		}
		setTimeText(formatElapsedTime(startedAt));
		const interval = setInterval(() => {
			setTimeText(formatElapsedTime(startedAt));
		}, 1000);
		return () => clearInterval(interval);
	}, [startedAt, showTime]);

	return timeText;
}

function renderActivityIcon(activity: ActivityData) {
	const lower = activity.name.toLowerCase();
	if (lower.includes('spotify')) {
		return <SpotifyLogoIcon size={28} weight="fill" color="#1DB954" />;
	}
	if (lower.includes('code') || lower.includes('vs code')) {
		return <CodeIcon size={28} weight="bold" color="#007ACC" />;
	}
	switch (activity.type) {
		case 'listening':
			return <HeadphonesIcon size={28} weight="fill" color="#1DB954" />;
		case 'streaming':
			return <BroadcastIcon size={28} weight="bold" color="#9146FF" />;
		case 'watching':
			return <TelevisionIcon size={28} weight="fill" color="#FF0000" />;
		case 'competing':
			return <TrophyIcon size={28} weight="fill" color="#FFD700" />;
		case 'playing':
		default:
			return <GameControllerIcon size={28} weight="fill" color="#5865F2" />;
	}
}

export const RichPresenceCard: React.FC<RichPresenceCardProps> = observer(({activity, className}) => {
	const headerText = getActivityHeader(activity.type);
	const timeText = useElapsedTime(activity.startedAt, activity.showTime ?? true);

	return (
		<div className={clsx(styles.container, className)} data-flx="presence.rich-presence-card.container">
			<div className={styles.header} data-flx="presence.rich-presence-card.header">
				<span className={styles.headerDot} />
				<span>{headerText}</span>
			</div>
			<div className={styles.content} data-flx="presence.rich-presence-card.content">
				<div className={styles.iconWrapper} data-flx="presence.rich-presence-card.icon-wrapper">
					{renderActivityIcon(activity)}
				</div>
				<div className={styles.info} data-flx="presence.rich-presence-card.info">
					<span className={styles.name} title={activity.name}>
						{activity.name}
					</span>
					{activity.details && (
						<span className={styles.details} title={activity.details}>
							{activity.details}
						</span>
					)}
					{activity.state && (
						<span className={styles.state} title={activity.state}>
							{activity.state}
						</span>
					)}
					{timeText && (
						<div className={styles.time} data-flx="presence.rich-presence-card.time">
							<ClockIcon size={12} weight="regular" className={styles.timeIcon} />
							<span>{timeText}</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
});

export default RichPresenceCard;
